import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApolloClient } from "@apollo/client";

import {
  ppClaimSeedFromRecord,
  type PpClaimMemberSeed,
  type PpClaimRecordLike,
} from "../members/toClaimContext";
import {
  resolvePpMemberRecord,
  type LoadedMemberForResolve,
} from "../members/resolvePpMembId";

/**
 * Resolves the on-screen member to ONE Provider Portal member row and hands back
 * the Create Claim seed built from it — `membKeyId`, `hpCode` and `startDate`
 * together, from that row, or nothing at all.
 *
 * WHY THIS EXISTS. The claim shell endpoint's stored proc matches the member on
 * the composite `(RmiMembKeyId, RmiCompanyId, RmiStartDate, RmiHpCode)` and
 * INSERTs regardless of whether that matched, so a wrong or mixed-source trio
 * yields an HTTP 200 with a blank-patient claim rather than an error. Scavenging
 * the three values off whatever member object the profile happens to be showing
 * cannot produce a matching composite: a Pathways (Mongo/DBX) member carries no
 * `RmiStartDate` at all, and its `healthplanCode` is a different system's
 * vocabulary. The only sound answer is the real PP row — see
 * members/resolvePpMembId.ts's `resolvePpMemberRecord`.
 *
 * It is deliberately NOT gated on the user having a linked Provider Portal
 * account: care-management-gql's `resolvePpBearer` mints a PP service-account
 * token for Entra-SSO callers, so this resolution works for an unlinked SSO user.
 * A missing USER-level PP token blocks separately and for a different reason —
 * the claim-write provider list's `no-identity` status.
 *
 * FAILS CLOSED. Only `"resolved"` carries a seed; every other status is a block.
 */
export type PpClaimMemberSeedStatus =
  /** Lookup in flight. Blocks, but transiently — not a denial. */
  | "resolving"
  /** One PP row resolved and it carries all three required values. */
  | "resolved"
  /**
   * PP was reached and answered, but no single row could be matched to this
   * member (or the matched row was missing one of the three values). A real
   * answer about the data, not a failure — and a hard block either way.
   */
  | "unresolved"
  /** The lookup itself failed (network / auth / GraphQL error). Retryable. */
  | "error";

export interface UsePpClaimMemberSeedArgs {
  /**
   * The APP-level Apollo client (MemberProfilePage's `useApolloClient()`), not
   * the one inside @astrana/ui's own GraphQLProvider subtree. `null` is treated
   * as unresolvable rather than as an error.
   */
  client: ApolloClient<unknown> | null;
  /**
   * Identity inputs for the crosswalk. `null` when the profile has nothing to
   * search on at all (no name, no DOB, no member key) — reported as
   * `"unresolved"` with no request fired.
   */
  member: LoadedMemberForResolve | null;
  /**
   * A PP member row the host ALREADY holds — the search row a PP-path profile
   * carries in `location.state`. When it is complete it is used verbatim and NO
   * lookup runs: it is the same `vRmiMemberSearch` row this hook would resolve,
   * already row-atomic, and it is the span the user themselves picked in the
   * search results. An incomplete one (e.g. a hard-refreshed PP profile, whose
   * URL carries no health plan) is ignored and resolution proceeds.
   */
  preset?: PpClaimRecordLike | null;
}

export interface UsePpClaimMemberSeedResult {
  status: PpClaimMemberSeedStatus;
  /** Non-null ONLY when status is `"resolved"`. */
  seed: PpClaimMemberSeed | null;
  reload: () => void;
}

export function usePpClaimMemberSeed({
  client,
  member,
  preset,
}: UsePpClaimMemberSeedArgs): UsePpClaimMemberSeedResult {
  const [status, setStatus] = useState<PpClaimMemberSeedStatus>("resolving");
  const [seed, setSeed] = useState<PpClaimMemberSeed | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const presetSeed = useMemo(
    () => ppClaimSeedFromRecord(preset),
    // Keyed on the row's own fields, not the object identity: a host that
    // rebuilds this object each render must not re-trigger anything.
    [preset?.membKeyId, preset?.healthPlanCode, preset?.startDate, preset?.gender],
  );

  // Read the member through a ref, and key the effect on its VALUES. The caller
  // builds this object inline from the loaded member, so its identity changes on
  // every render; depending on it directly would re-fire the lookup forever.
  const memberRef = useRef(member);
  memberRef.current = member;
  const memberKey = member
    ? [member.membId, member.memberKeyId, member.name, member.dob, member.ipaCode]
        .map((v) => v ?? "")
        .join("|")
    : "";

  useEffect(() => {
    // A complete in-hand row needs no lookup at all (see `preset`).
    if (presetSeed) return;
    let cancelled = false;
    setStatus("resolving");
    setSeed(null);
    const target = memberRef.current;
    if (!client || !target) {
      // Nothing to search on. This is the honest answer, not an error: there is
      // no request that could be made.
      setStatus("unresolved");
      return;
    }
    void (async () => {
      try {
        const record = await resolvePpMemberRecord(client, target);
        if (cancelled) return;
        const resolved = ppClaimSeedFromRecord(record);
        if (resolved) {
          setSeed(resolved);
          setStatus("resolved");
          return;
        }
        // Distinguish the two shapes of "unresolved" in the log only — the user
        // gets one message and the telemetry one reason, both PHI-free.
        console.warn(
          record
            ? "[usePpClaimMemberSeed] matched PP row is missing membKeyId/hpCode/startDate"
            : "[usePpClaimMemberSeed] no single PP member row matched",
        );
        setStatus("unresolved");
      } catch (error) {
        if (cancelled) return;
        console.warn("[usePpClaimMemberSeed] PP member lookup failed", error);
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, memberKey, presetSeed, attempt]);

  // The preset short-circuits the state machine entirely, so a PP-path member
  // never renders a "resolving" flash for a row already in hand.
  if (presetSeed) return { status: "resolved", seed: presetSeed, reload };
  return { status, seed: status === "resolved" ? seed : null, reload };
}
